import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { useEnvironment } from '../contexts/EnvironmentContext';
import {
  BookOpen,
  Key,
  Package,
  Rocket,
  ShieldCheck,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Layers,
  Wallet,
  Activity,
  CheckCircle2,
  X,
  Search,
  Zap,
} from 'lucide-react';
import type { Product, Plan, Application, Subscription } from '../types';

interface ProductWithPlans extends Product {
  plans: Plan[];
}

interface WalletData {
  balance: number;
  currency: string;
}

export const Home: React.FC = () => {
  const { isAuthenticated, client } = useAuthStore();
  const { environment } = useEnvironment();
  const navigate = useNavigate();

  // App Selection Modal state
  const [selectedProduct, setSelectedProduct] = useState<ProductWithPlans | null>(null);
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [publicSearch, setPublicSearch] = useState('');

  // 1. Featured APIs Query (Runs publicly to allow catalog exploration)
  const { data: featuredData, isLoading: isFeaturedLoading } = useQuery({
    queryKey: ['featured_catalog_products'],
    queryFn: async () => {
      const response = await api.get<{ data: ProductWithPlans[] }>('/products?limit=12');
      return response.data.data;
    },
  });

  // 2. Client Applications Query
  const { data: clientApps = [] } = useQuery({
    queryKey: ['client_applications_home', client?.id],
    queryFn: async () => {
      if (!client?.id) return [];
      const res = await api.get<{ data: Application[] }>(
        `/clients/${client.id}/applications?limit=50`
      );
      return res.data.data || [];
    },
    enabled: !!client?.id && isAuthenticated,
  });

  // 3. Client Subscriptions Query
  const { data: subscriptions = [] } = useQuery({
    queryKey: ['client_subscriptions_home', client?.id, environment],
    queryFn: async () => {
      if (!client?.id) return [];
      const res = await api.get<{ data: Subscription[] }>(
        `/clients/${client.id}/subscriptions?limit=100`
      );
      return res.data.data || [];
    },
    enabled: !!client?.id && isAuthenticated,
  });

  // 4. Client Wallet Query
  const { data: walletData } = useQuery({
    queryKey: ['client_wallet_home', client?.id],
    queryFn: async () => {
      if (!client?.id) return null;
      try {
        const res = await api.get<{ data: WalletData }>(`/wallet/balance/${client.id}`);
        return res.data.data;
      } catch {
        return { balance: 0, currency: 'AOA' };
      }
    },
    enabled: !!client?.id && isAuthenticated,
  });

  const featuredProducts = featuredData || [];
  const activeSubsCount = subscriptions.filter((s) => s.status === 'active').length;

  const filteredPublicProducts = useMemo(() => {
    if (!featuredData) return [];
    if (!publicSearch.trim()) return featuredData;
    const term = publicSearch.toLowerCase();
    return featuredData.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        (p.description && p.description.toLowerCase().includes(term))
    );
  }, [featuredData, publicSearch]);

  const handleNavigateCatalog = () => {
    if (clientApps.length > 0) {
      navigate(`/applications/${clientApps[0].id}/catalog`);
    } else {
      navigate('/applications');
    }
  };

  const handleNavigateSubscriptions = () => {
    if (clientApps.length > 0) {
      navigate(`/applications/${clientApps[0].id}/subscriptions`);
    } else {
      navigate('/applications');
    }
  };

  const handleProductAction = (product: ProductWithPlans) => {
    if (clientApps.length === 0) {
      navigate('/applications');
    } else if (clientApps.length === 1) {
      navigate(`/applications/${clientApps[0].id}/products/${product.id}`);
    } else {
      setSelectedProduct(product);
      setIsAppModalOpen(true);
    }
  };

  const handleSelectApp = (appId: string) => {
    if (!selectedProduct) return;
    setIsAppModalOpen(false);
    navigate(`/applications/${appId}/products/${selectedProduct.id}`);
  };

  if (isAuthenticated) {
    return (
      <div className="container-custom py-8 animate-fade-in space-y-12">
        {/* Welcome Banner */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-gradient-to-r from-secondary-600 via-secondary-500 to-primary-600 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 opacity-15 pointer-events-none">
            <Rocket className="w-64 h-64" />
          </div>
          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-xs font-semibold uppercase tracking-wider mb-3">
              <Sparkles className="w-3.5 h-3.5 text-amber-300" /> Portal do Desenvolvedor
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold mb-3">
              Bem-vindo(a), {client?.company_name || 'Desenvolvedor'}!
            </h1>
            <p className="text-base md:text-lg opacity-90 mb-6 leading-relaxed">
              Integre com segurança o nosso portfólio de APIs. Crie a sua aplicação, explore os
              catálogos disponíveis e subscreva aos produtos para obter chaves e documentação completa.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={() => navigate('/applications')}
                className="bg-white text-secondary-700 hover:bg-slate-100 px-6 py-3 rounded-xl font-bold transition-all shadow-md hover:shadow-lg flex items-center gap-2 text-sm"
              >
                Minhas Aplicações <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={handleNavigateCatalog}
                className="bg-white/15 hover:bg-white/25 text-white border border-white/30 backdrop-blur-sm px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 text-sm"
              >
                Explorar Catálogo Geral <Layers className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Cockpit KPIs / Executive Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* KPI 1: Aplicações */}
          <div
            onClick={() => navigate('/applications')}
            className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex justify-between items-start">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Aplicações
              </span>
              <div className="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                <Key className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-3xl font-extrabold text-slate-900 dark:text-white">
                {clientApps.length}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 ml-2 font-medium">
                configuradas
              </span>
            </div>
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-2 font-semibold flex items-center gap-1 group-hover:underline">
              Gerir chaves e credenciais <ArrowRight className="w-3 h-3" />
            </p>
          </div>

          {/* KPI 2: Subscrições */}
          <div
            onClick={handleNavigateSubscriptions}
            className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex justify-between items-start">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Subscrições Ativas
              </span>
              <div className="w-10 h-10 rounded-xl bg-secondary-50 dark:bg-secondary-950/40 text-secondary-600 dark:text-secondary-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                <Package className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-3xl font-extrabold text-slate-900 dark:text-white">
                {activeSubsCount}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 ml-2 font-medium">
                APIs contratadas
              </span>
            </div>
            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-2 font-semibold flex items-center gap-1 group-hover:underline">
              Ver subscrições e planos <ArrowRight className="w-3 h-3" />
            </p>
          </div>

          {/* KPI 3: Carteira */}
          <div
            onClick={() => navigate('/wallet')}
            className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all cursor-pointer group"
          >
            <div className="flex justify-between items-start">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Saldo da Carteira
              </span>
              <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center group-hover:scale-105 transition-transform">
                <Wallet className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-2xl font-extrabold text-slate-900 dark:text-white">
                {Number(walletData?.balance || 0).toLocaleString('pt-AO', {
                  minimumFractionDigits: 2,
                })}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400 ml-1 font-bold">
                {walletData?.currency || 'AOA'}
              </span>
            </div>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-semibold flex items-center gap-1 group-hover:underline">
              Carregar fundos / faturas <ArrowRight className="w-3 h-3" />
            </p>
          </div>

          {/* KPI 4: Gateway Status */}
          <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
            <div className="flex justify-between items-start">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Gateway & Ambiente
              </span>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                <Activity className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
              </span>
              <span className="text-base font-bold text-slate-900 dark:text-white capitalize">
                {environment} Ativo
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              Kong Gateway v3 • Porta 8000
            </p>
          </div>
        </div>

        {/* Stepper / Integration Flow */}
        <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Fluxo de Integração</h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
              Quatro passos simples para colocar as suas integrações em funcionamento
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-slate-100 dark:bg-slate-800 -translate-y-1/2 z-0" />

            {[
              {
                step: 1,
                title: 'Criar Aplicação',
                desc: 'Gere chaves e credenciais seguras',
                icon: Key,
                action: () => navigate('/applications'),
              },
              {
                step: 2,
                title: 'Explorar Catálogo',
                desc: 'Descubra as APIs e especificações',
                icon: BookOpen,
                action: handleNavigateCatalog,
              },
              {
                step: 3,
                title: 'Subscrever Produto',
                desc: 'Selecione o plano mais adequado',
                icon: Package,
                action: handleNavigateCatalog,
              },
              {
                step: 4,
                title: 'Integrar & Testar',
                desc: 'Consuma via Sandbox ou Produção',
                icon: Rocket,
                action: () => navigate('/applications'),
              },
            ].map((item, idx) => (
              <div
                key={idx}
                onClick={item.action}
                className="relative z-10 flex flex-col items-center text-center group cursor-pointer"
              >
                <div className="w-16 h-16 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 group-hover:border-secondary-500 flex items-center justify-center shadow-md mb-4 text-slate-700 dark:text-slate-300 group-hover:text-secondary-600 dark:group-hover:text-secondary-400 group-hover:scale-105 transition-all">
                  <item.icon className="w-7 h-7" />
                </div>
                <h3 className="font-bold text-slate-900 dark:text-white text-base group-hover:text-secondary-600 dark:group-hover:text-secondary-400 transition-colors">
                  {item.step}. {item.title}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Featured APIs Section */}
        <section id="catalogo-geral" className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400 text-xs font-bold uppercase tracking-wider mb-2">
                <Sparkles className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> Seleção Recomendada
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-slate-900 dark:text-white">
                Catálogo em Destaque
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
                APIs de alto desempenho e maior procura pelos nossos parceiros e desenvolvedores.
              </p>
            </div>

            <button
              onClick={handleNavigateCatalog}
              className="text-primary-600 dark:text-primary-400 font-bold text-sm hover:underline inline-flex items-center gap-1 self-start sm:self-auto"
            >
              Ver todas as APIs no catálogo <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {isFeaturedLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  className="animate-pulse bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 h-64"
                />
              ))}
            </div>
          ) : featuredProducts.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-12 text-center border border-slate-200 dark:border-slate-800">
              <Package className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 dark:text-slate-400 font-medium">
                Nenhum produto em destaque no momento.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredProducts.map((product) => {
                const plans = product.plans || [];
                const freePlan = plans.find((p) => Number(p.monthly_price) === 0);
                const cheapestPaidPlan = plans
                  .filter((p) => Number(p.monthly_price) > 0)
                  .sort((a, b) => Number(a.monthly_price) - Number(b.monthly_price))[0];

                return (
                  <div
                    key={product.id}
                    className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm hover:shadow-xl transition-all flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-secondary-500 to-primary-500 flex items-center justify-center text-white font-bold text-lg shadow-md">
                          {product.name.slice(0, 2).toUpperCase()}
                        </div>
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40">
                          {product.authentication_provider || 'API Key'}
                        </span>
                      </div>

                      <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-2 group-hover:text-primary-600 transition-colors">
                        {product.name}
                      </h3>
                      <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-3 mb-4 leading-relaxed">
                        {product.description || 'API disponível para integração imediata.'}
                      </p>

                      <div className="space-y-1.5 pt-3 border-t border-slate-100 dark:border-slate-800 mb-6">
                        {freePlan && (
                          <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            <span>Plano Gratuito ({freePlan.rate_limit_per_minute} req/min)</span>
                          </div>
                        )}
                        {cheapestPaidPlan && (
                          <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                            <CheckCircle2 className="w-3.5 h-3.5 text-secondary-500 shrink-0" />
                            <span>
                              Desde {Number(cheapestPaidPlan.monthly_price).toLocaleString('pt-AO')} AOA/mês
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => handleProductAction(product)}
                      className="w-full py-2.5 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs shadow-md transition-all flex items-center justify-center gap-2"
                    >
                      Subscrever / Ver Detalhes <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Application Selection Modal */}
        {isAppModalOpen && selectedProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full border border-slate-200 dark:border-slate-800 shadow-2xl p-6 animate-scale-up">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
                <div>
                  <h3 className="font-bold text-lg text-slate-900 dark:text-white">
                    Selecionar Aplicação
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Para subscrever a {selectedProduct.name}
                  </p>
                </div>
                <button
                  onClick={() => setIsAppModalOpen(false)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="py-4 space-y-2 max-h-60 overflow-y-auto">
                {clientApps.map((app) => (
                  <button
                    key={app.id}
                    onClick={() => handleSelectApp(app.id)}
                    className="w-full p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-primary-500 text-left transition-all hover:bg-slate-50 dark:hover:bg-slate-800/50 flex items-center justify-between group"
                  >
                    <div>
                      <h4 className="font-bold text-sm text-slate-900 dark:text-white group-hover:text-primary-600">
                        {app.name}
                      </h4>
                      <span className="text-[10px] text-slate-500">
                        {app.sandbox_mode ? 'Sandbox' : 'Produção'}
                      </span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>

              <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
                <button
                  onClick={() => {
                    setIsAppModalOpen(false);
                    navigate('/applications');
                  }}
                  className="text-xs font-semibold text-secondary-600 dark:text-secondary-400 hover:underline"
                >
                  + Criar Nova Aplicação
                </button>
                <button
                  onClick={() => setIsAppModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Logged out state (Landing Page & Public Marketplace Showcase)
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-primary-500/10 dark:bg-primary-500/5 blur-[120px] rounded-full pointer-events-none" />

        {/* Hero Section */}
        <div className="text-center mb-16 relative z-10 space-y-6">
          <Link
            to="/status"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 text-xs font-bold shadow-xs hover:shadow-md transition-all hover:-translate-y-0.5"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Kong Gateway Operacional • 99.99% Uptime SLA • Aceleração Cache Ativa
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            A sua porta de entrada para <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 via-secondary-500 to-indigo-600">
              APIs de Alta Performance
            </span>
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Descubra, teste e integre serviços mission-critical no nosso ecossistema de APIs acelerado por Kong Gateway. Crie aplicações corporativas seguras com suporte a Sandbox e cobrança transparente.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-2">
            <Link
              to="/register"
              className="btn-base px-8 py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl shadow-lg hover:shadow-xl text-base w-full sm:w-auto font-bold flex items-center justify-center gap-2"
            >
              Criar Conta Gratuita <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#catalogo-publico"
              className="btn-base px-8 py-3.5 bg-white dark:bg-slate-900 text-slate-800 dark:text-white border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl shadow-sm text-base w-full sm:w-auto font-bold flex items-center justify-center gap-2"
            >
              Explorar Catálogo <Layers className="w-4 h-4 text-primary-500" />
            </a>
            <Link
              to="/login"
              className="btn-base px-6 py-3.5 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-base w-full sm:w-auto font-semibold"
            >
              Iniciar Sessão
            </Link>
          </div>
        </div>

        {/* Public API Catalog Showcase Section */}
        <section id="catalogo-publico" className="pt-8 pb-16 space-y-8 relative z-10 scroll-mt-24">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-6">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-primary-50 dark:bg-primary-950/60 text-primary-700 dark:text-primary-300 border border-primary-200/80 dark:border-primary-800/80 mb-2">
                <Sparkles className="w-3.5 h-3.5" /> Portfólio de APIs Disponíveis
              </div>
              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
                Catálogo de APIs & Serviços
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
                Explore os endpoints publicados, limites de requisições e planos disponíveis para integração imediata.
              </p>
            </div>

            {/* Search Input */}
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Pesquisar por nome ou funcionalidade..."
                value={publicSearch}
                onChange={(e) => setPublicSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl text-xs sm:text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 shadow-xs"
              />
            </div>
          </div>

          {/* Grid of Public APIs */}
          {isFeaturedLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-64 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 animate-pulse p-6"
                />
              ))}
            </div>
          ) : filteredPublicProducts.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center space-y-3">
              <Layers className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto" />
              <h3 className="font-bold text-slate-800 dark:text-slate-200 text-base">
                Nenhuma API encontrada
              </h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                {publicSearch
                  ? `Não encontramos resultados para "${publicSearch}". Tente outro termo de busca.`
                  : 'O catálogo está a ser atualizado. Novos produtos serão publicados em breve.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPublicProducts.map((product) => {
                const freePlan = product.plans?.find(
                  (p) => Number(p.monthly_price) === 0 || p.tier === 'basic'
                );
                const cheapestPaid = product.plans
                  ?.filter((p) => Number(p.monthly_price) > 0)
                  .sort((a, b) => Number(a.monthly_price) - Number(b.monthly_price))[0];

                return (
                  <div
                    key={product.id}
                    className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm hover:shadow-xl transition-all flex flex-col justify-between group hover:-translate-y-1"
                  >
                    <div className="space-y-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-secondary-500 to-primary-600 flex items-center justify-center text-white font-black text-lg shadow-md shrink-0">
                          {product.name.slice(0, 2).toUpperCase()}
                        </div>
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40">
                          {product.authentication_provider || 'API Key'}
                        </span>
                      </div>

                      <div>
                        <h3 className="font-bold text-lg text-slate-900 dark:text-white group-hover:text-primary-600 transition-colors">
                          {product.name}
                        </h3>
                        <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-3 mt-1.5 leading-relaxed">
                          {product.description || 'API otimizada para integração via Kong Gateway.'}
                        </p>
                      </div>

                      <div className="space-y-1.5 pt-3 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-300">
                        {freePlan && (
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            <span>Plano Sandbox ({freePlan.rate_limit_per_minute} req/min)</span>
                          </div>
                        )}
                        {cheapestPaid && (
                          <div className="flex items-center gap-2">
                            <Zap className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                            <span>A partir de {Number(cheapestPaid.monthly_price).toLocaleString('pt-AO')} AOA/mês</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="pt-6">
                      <Link
                        to="/register"
                        className="w-full py-2.5 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs shadow-xs hover:shadow-md transition-all flex items-center justify-center gap-2"
                      >
                        Registar para Integrar <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Enterprise Value Props Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 relative z-10 border-t border-slate-200 dark:border-slate-800 pt-16">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm hover:shadow-md p-8 border border-slate-200/80 dark:border-slate-800 transition-all">
            <div className="w-14 h-14 bg-secondary-100 dark:bg-secondary-900/30 text-secondary-600 dark:text-secondary-400 rounded-2xl flex items-center justify-center mb-6 shadow-xs">
              <Rocket className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              Integração Instantânea
            </h3>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-sm">
              Documentação OpenAPI 3.0 interativa, snippets de código gerados em 8 linguagens e ambiente de testes API Tester embutido.
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm hover:shadow-md p-8 border border-slate-200/80 dark:border-slate-800 transition-all">
            <div className="w-14 h-14 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-2xl flex items-center justify-center mb-6 shadow-xs">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              Seguro & Confiável
            </h3>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-sm">
              Validação estrita de chaves de API pelo Kong Gateway, isolamento de ambientes Sandbox e Produção e políticas automáticas de CORS.
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm hover:shadow-md p-8 border border-slate-200/80 dark:border-slate-800 transition-all">
            <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mb-6 shadow-xs">
              <TrendingUp className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
              Escala & Aceleração Cache
            </h3>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-sm">
              Respostas GET aceleradas em memória com proxy cache Kong (&lt;5ms), controle proativo de quota mensal e recibos oficiais de subscrição.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
